import { createFileRoute } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { Heading } from "@/components/heading";
import { type Preference, usePreferences } from "@novu/react";
import { NovuProvider } from "@/utils/novu-provider";
import { Fragment, useMemo } from "react";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { Icon } from "@/components/icon";

export const Route = createFileRoute(
	"/_visibility/_topnav/notificacoes/configuracoes",
)({
	component: RouteComponent,
});

function NotificationPreferencesTable() {
	const { preferences } = usePreferences();

	const templatePreferencesIndex = useMemo(() => {
		return preferences?.reduce<Record<string, Preference>>((acc, pref) => {
			const { level, workflow } = pref;

			if (level !== "template" || !workflow?.identifier) {
				return acc;
			}

			acc[workflow.identifier] = pref;

			return acc;
		}, {});
	}, [preferences]);

	const notificationTopics = [
		{
			name: "Comunidade",
			notifications: [
				{ template: "WeeklyDigest", name: "Resumo semanal" },
				{ template: "PostComment", name: "Novo comentário no seu post" },
				{ template: "CommentReply", name: "Nova resposta no seu comentário" },
				{ template: "CommentMention", name: "Menção em comentário" },
				{ template: "PostMention", name: "Menção em post" },
				{
					template: "PostCreated",
					name: "Post importante",
					tooltip: "Posts que os administradores escolhem enviar por email",
				},
			],
		},
		{
			name: "Eventos",
			notifications: [
				{ template: "EventPromotion", name: "Novo evento" },
				{ template: "EventUpdated", name: "Evento atualizado" },
				{ template: "EventCanceled", name: "Evento cancelado" },
				{ template: "EventReminder", name: "Lembrete de evento" },
			],
		},
		{
			name: "Cursos",
			notifications: [
				{ template: "CourseLessonCreated", name: "Nova aula" },
				{ template: "CourseLessonUpdated", name: "Aula atualizada" },
			],
		},
	];

	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>&nbsp;</TableHead>

					<TableHead className="text-center">Email</TableHead>

					<TableHead className="text-center">In-app</TableHead>
				</TableRow>
			</TableHeader>

			<TableBody>
				{notificationTopics.map((not) => (
					<Fragment key={not.name}>
						<TableRow>
							<TableCell className="font-semibold bg-muted" colSpan={3}>
								{not.name}
							</TableCell>
						</TableRow>

						{not.notifications?.map((n) => (
							<TableRow key={n.template}>
								<TableCell className="flex items-center justify-start gap-2">
									{n.name}
									{n.tooltip ? (
										<Tooltip>
											<TooltipTrigger>
												<Icon
													name="Info"
													className="size-4 text-muted-foreground"
												/>
											</TooltipTrigger>

											<TooltipContent>{n.tooltip}</TooltipContent>
										</Tooltip>
									) : null}
								</TableCell>

								<TableCell className="text-center">
									<Checkbox
										checked={
											templatePreferencesIndex?.[n.template]?.channels?.email ||
											false
										}
										disabled={
											!(
												"email" in
												(templatePreferencesIndex?.[n.template]?.channels || {})
											)
										}
										onCheckedChange={async (val) => {
											if (templatePreferencesIndex?.[n.template]) {
												await templatePreferencesIndex?.[n.template].update({
													channels: {
														email: Boolean(val),
													},
												});
											}
										}}
									/>
								</TableCell>

								<TableCell className="text-center">
									<Checkbox
										checked={
											templatePreferencesIndex?.[n.template]?.channels
												?.in_app || false
										}
										disabled={
											!(
												"in_app" in
												(templatePreferencesIndex?.[n.template]?.channels || {})
											)
										}
										onCheckedChange={async (val) => {
											if (templatePreferencesIndex?.[n.template]) {
												await templatePreferencesIndex?.[n.template].update({
													channels: {
														in_app: Boolean(val),
													},
												});
											}
										}}
									/>
								</TableCell>
							</TableRow>
						))}
					</Fragment>
				))}
			</TableBody>
		</Table>
	);
}

function RouteComponent() {
	return (
		<NovuProvider>
			<div className="w-full h-full flex items-center justify-center px-6">
				<div className="flex flex-col w-full max-w-[700px] pb-6">
					<div className="flex max-sm:flex-col gap-6 flex-row items-baseline justify-between mb-6 mt-6">
						<Heading>Preferências de Notificação</Heading>
					</div>

					<Card className="p-6 py-1.5 w-full max-md:px-4 max-w-[700px]">
						<NotificationPreferencesTable />
					</Card>
				</div>
			</div>
		</NovuProvider>
	);
}
